import { WppButton, WppCard, WppIconInfo, WppTypography } from '@platform-ui-kit/components-library-react'
import { useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useInviteNewUserApi } from 'api/users/mutations/useInviteUserApi'
import { is400Error } from 'api/utils'
import { Flex } from 'components/common/flex/Flex'
import { NewUserDetailsParams } from 'components/form/formAutocomplete/FormUsersListAutocomplete'
import { FormGroupsSelect } from 'components/form/formSelect/FormGroupsSelect'
import { FormTextInput } from 'components/form/formTextInput/FormTextInput'
import { FormToggle } from 'components/form/formToggle/FormToggle'
import { SideModal } from 'components/surface/sideModal/SideModal'
import { useForm } from 'hooks/form/useForm'
import styles from 'pages/admin/members/addNewMemberSideModal/AddNewMemberSideModal.module.scss'
import {
  getDefaultValues,
  transformDomainsList,
  useValidationScheme,
} from 'pages/admin/members/addNewMemberSideModal/utils'
import { handleReloadMembersTable } from 'pages/admin/members/membersList/membersTable/utils'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { useToast } from 'providers/toast/ToastProvider'
import { createNiceModal, NiceModalWrappedProps } from 'utils/createNiceModal'

interface Props extends NiceModalWrappedProps {
  title?: string
  withBackButton?: boolean
  onSuccess?: (user: NewUserDetailsParams) => void
}

const AddNewMemberSideModal = ({
  id,
  title,
  isOpen,
  onClose,
  onSuccess,
  onCloseComplete,
  withBackButton = false,
}: Props) => {
  const { t } = useTranslation()
  const { enqueueToast } = useToast()

  const { currentTenant, tenantDomains } = useCurrentTenantData()
  const { mutateAsync: handleInviteNewUser } = useInviteNewUserApi()
  const transformedDomains = useMemo(() => transformDomainsList(tenantDomains), [tenantDomains])

  const form = useForm({
    defaultValues: getDefaultValues(),
    validationSchema: useValidationScheme(tenantDomains),
  })

  const {
    setError,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form

  const onError = (error: any) => {
    let message = t('os.common.errors.general')
    if (is400Error(error)) {
      setError('email', { message: t('os.members.add_new_member.email.unique_error') }, { shouldFocus: true })
      message = error.response?.data?.detail || t('os.common.errors.general')
    }
    enqueueToast({ message, type: 'error' })
  }

  const onSubmit = handleSubmit(async values => {
    try {
      await handleInviteNewUser({ data: values, tenantId: currentTenant.id })
      onSuccess?.({ ...values, synced: false } as NewUserDetailsParams)
      handleReloadMembersTable()
      enqueueToast({
        message: t('os.members.add_new_member.success_add_message'),
        type: 'success',
      })
      onClose()
    } catch (e: any) {
      onError(e)
    }
  })

  return (
    <FormProvider {...form}>
      <SideModal
        size="l"
        open={isOpen}
        data-testid={id}
        disableOutsideClick
        formConfig={{ onSubmit }}
        onWppSideModalClose={onClose}
        withBackButton={withBackButton}
        onWppSideModalBackButtonClick={onClose}
        onWppSideModalCloseComplete={onCloseComplete}
      >
        <WppTypography slot="header" type="2xl-heading">
          {title}
        </WppTypography>

        <Flex slot="body" direction="column" gap={24} className={styles.modal}>
          <WppCard size="l" className={styles.warningCard}>
            <Flex gap={10}>
              <Flex>
                <WppIconInfo />
              </Flex>
              <Flex direction="column" gap={10}>
                <WppTypography type="m-strong">{t('os.members.add_new_member.domains_warning.title')}</WppTypography>
                <Flex direction="column">
                  <WppTypography>{t('os.members.add_new_member.domains_warning.description')}</WppTypography>
                  <WppTypography>{transformedDomains}</WppTypography>
                </Flex>
              </Flex>
            </Flex>
          </WppCard>
          <FormTextInput
            required
            name="email"
            type="email"
            disabled={isSubmitting}
            placeholder={t('os.members.add_new_member.email.placeholder')}
            labelConfig={{
              text: t('os.members.add_new_member.email.label'),
            }}
            messageType="warning"
          />
          <FormTextInput
            required
            name="firstname"
            disabled={isSubmitting}
            placeholder={t('os.members.add_new_member.first_name.placeholder')}
            labelConfig={{
              text: t('os.members.add_new_member.first_name.label'),
            }}
          />
          <FormTextInput
            required
            name="lastname"
            disabled={isSubmitting}
            placeholder={t('os.members.add_new_member.last_name.placeholder')}
            labelConfig={{
              text: t('os.members.add_new_member.last_name.label'),
            }}
          />

          <FormToggle
            required
            name="isExternal"
            labelConfig={{ text: t('os.members.add_new_member.external_member') }}
          />

          <FormGroupsSelect
            type="multiple"
            name="groupsMembership"
            className={styles.groupSelect}
            getOptionValue={({ id }) => id}
            dropdownConfig={{
              popperOptions: {
                modifiers: [
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: ['bottom'],
                    },
                  },
                ],
              },
            }}
            labelConfig={{
              text: t('os.members.add_members_to_os.groups.label'),
              description: t('os.members.add_members_to_os.groups.label_description'),
            }}
            placeholder={t('os.members.add_members_to_os.groups.placeholder')}
          />
        </Flex>

        <Flex slot="actions" gap={12} justify="end">
          <WppButton variant="secondary" onClick={onClose} data-testid="cancel">
            {t('os.common.cancel')}
          </WppButton>
          <WppButton type="submit" variant="primary" data-testid="apply" disabled={!isValid} loading={isSubmitting}>
            {t('os.groups.view_group.members_table.add_members')}
          </WppButton>
        </Flex>
      </SideModal>
    </FormProvider>
  )
}

export const { showModal: showAddNewMemberSideModal } = createNiceModal(AddNewMemberSideModal, 'add-new-member')
